import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useData } from '../../lib/DataContext.jsx';
import { X, ChevronDown } from 'lucide-react';

const BANDS = ['Excellent', 'Good', 'Moderate', 'Poor'];

export default function FilterBar() {
  const { data, filtered, filters, setType, setRegions, setBands } = useData();
  const [sp, setSp] = useSearchParams();

  // URL sync: read on mount, write on change
  useEffect(() => {
    const t = sp.get('type');
    const r = sp.get('regions');
    const b = sp.get('bands');
    if (t && ['All', 'HRDP', 'FDP'].includes(t)) setType(t);
    if (r) setRegions(new Set(r.split(',').filter(Boolean)));
    if (b) setBands(new Set(b.split(',').filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (filters.type !== 'All') next.set('type', filters.type); else next.delete('type');
    if (filters.regions.size) next.set('regions', [...filters.regions].join(',')); else next.delete('regions');
    if (filters.bands.size) next.set('bands', [...filters.bands].join(',')); else next.delete('bands');
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.type, filters.regions, filters.bands]);

  if (!data) return null;
  const total = data.allProjects.length;
  const regionsList = [...new Set(data.allProjects.map((p) => p.region))].sort();

  const clearAll = () => { setType('All'); setRegions(new Set()); setBands(new Set()); };
  const anyFilter = filters.type !== 'All' || filters.regions.size || filters.bands.size;

  return (
    <div className="filterbar" data-testid="filter-bar">
      <div className="seg" role="tablist" aria-label="Project type">
        {['All', 'HRDP', 'FDP'].map((t) => (
          <button
            key={t}
            className={filters.type === t ? 'on' : ''}
            onClick={() => setType(t)}
            data-testid={`filter-type-${t}`}
            aria-pressed={filters.type === t}
          >
            {t}
          </button>
        ))}
      </div>

      <MultiDropdown
        label="Region"
        options={regionsList}
        selected={filters.regions}
        onChange={setRegions}
        testId="filter-region"
      />
      <MultiDropdown
        label="Band"
        options={BANDS}
        selected={filters.bands}
        onChange={setBands}
        testId="filter-band"
      />

      {anyFilter ? (
        <button className="fdd-btn" onClick={clearAll} data-testid="filter-clear" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}>
          <X size={12} /> Clear all
        </button>
      ) : null}

      <div style={{ marginLeft: 'auto', color: 'var(--ink-3)', fontSize: 12.5, fontWeight: 600 }} data-testid="filter-count">
        Showing <span style={{ color: 'var(--ink)' }}>{filtered.length}</span> of {total}
      </div>
    </div>
  );
}

function MultiDropdown({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = (v) => {
    const n = new Set(selected);
    if (n.has(v)) n.delete(v); else n.add(v);
    onChange(n);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className={`fdd-btn ${selected.size ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-btn`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label} {selected.size ? `· ${selected.size}` : ''}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          role="listbox"
          data-testid={`${testId}-menu`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: '#fff',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: 'var(--shadow)',
            padding: 6,
            minWidth: 220,
            zIndex: 30,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {options.map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                fontSize: 13, color: 'var(--ink-2)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => toggle(opt)}
                data-testid={`${testId}-opt-${opt}`}
              />
              {opt}
            </label>
          ))}
          {selected.size > 0 && (
            <button
              onClick={() => onChange(new Set())}
              style={{
                width: '100%', marginTop: 4, padding: '6px', border: 0,
                background: 'var(--accent-soft)', color: 'var(--accent-2)',
                borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer',
              }}
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
